import React from 'react';
import { TrevorIcon } from '@trevor/ui-components';
import { createCustomEvent } from '@/util/createCustomEvent';
import { ERASE_PIN } from '@/constants/custom-event-names';
import { useEventListener } from '@/hooks';

const erasePinEvent = createCustomEvent(ERASE_PIN);

const usePinInput = (nextInputRef?: React.RefObject<HTMLInputElement>) => {
  const [pinChar, setPinChar] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const resetPinChar = () => setPinChar('');

  useEventListener(ERASE_PIN, resetPinChar);
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    if (val) {
      setPinChar(val);
      nextInputRef?.current?.focus();
    }
  };

  return [pinChar, handleInput, inputRef] as const;
};

const PinInput: React.FC<{ hideLogo?: boolean; onPinReady: F<string> }> = ({
  hideLogo,
  onPinReady,
}) => {
  const [pinChar5, handleCharInput5, ref5] = usePinInput();
  const [pinChar4, handleCharInput4, ref4] = usePinInput(ref5);
  const [pinChar3, handleCharInput3, ref3] = usePinInput(ref4);
  const [pinChar2, handleCharInput2, ref2] = usePinInput(ref3);
  const [pinChar1, handleCharInput1, ref1] = usePinInput(ref2);

  React.useEffect(() => {
    if (pinChar1 && pinChar2 && pinChar3 && pinChar4 && pinChar5) {
      onPinReady(`${pinChar1}${pinChar2}${pinChar3}${pinChar4}${pinChar5}`);
    }
  }, [pinChar1, pinChar2, pinChar3, pinChar4, pinChar5]);

  const handlePaste = (e: React.ClipboardEvent) => {
    const value = e.clipboardData.getData('text').trim();
    if (value.length === 5) {
      onPinReady(value);
    }
  };

  const handleErasePin = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      window.dispatchEvent(erasePinEvent);
      ref1.current?.focus();
    }
  };

  const inputs = [
    [pinChar1, handleCharInput1, ref1],
    [pinChar2, handleCharInput2, ref2],
    [pinChar3, handleCharInput3, ref3],
    [pinChar4, handleCharInput4, ref4],
    [pinChar5, handleCharInput5, ref5],
  ] as const;

  return (
    <div className="pin-container">
      {!hideLogo && (
        <a href="https://trevor.io">
          <TrevorIcon
            name="logo-wordmark"
            height="24"
            width="139.87"
            style={{ marginTop: '63px', marginBottom: '64px' }}
          />
        </a>
      )}
      <p>Enter your PIN</p>
      <div className="enter-pin">
        {inputs.map(([char, handleChange, ref], i) => (
          <input
            key={i}
            value={char}
            onChange={handleChange}
            ref={ref}
            name={`pin-char-${i + 1}`}
            onPaste={handlePaste}
            onKeyDown={handleErasePin}
            autoFocus={i === 0}
            type="password"
            autoComplete="off"
            maxLength={1}
          />
        ))}
      </div>
    </div>
  );
};

export default PinInput;
